import { Suspense, lazy } from "react"
import { ErrorBoundary } from "react-error-boundary"

import { lazyDefaultProps, fallbackColorByMode } from "./config"
import type { LazySpinnerProps, FallbackProps } from "./types"

import FallbackIcon from "@bounce/assets/icons/Spinner.svg"

const Spinner = lazy(() => import("./Spinner").then(m => ({ default: m.Spinner })))

export const DefaultFallback = ({ variant, size }: FallbackProps) => {
  return (
    <FallbackIcon
      color={fallbackColorByMode[variant]}
      {...(size === "auto"
        ? { style: { width: "100%", height: "100%" } } // 💅
        : { width: size, height: size })}
    />
  )
}

/**
 * **Props**:
 * - `size`: number | "auto" — *size of the spinner in pixels.*
 * - `variant`: "default" | "dark" | "light" — *color variant of the spinner.*
 * - `loadingJsx`: React.ReactNode — *JSX to render while loading.*
 * - `errorJsx`: React.ReactNode — *JSX to render on error.*
 *
 * See also: {@link LazySpinnerProps}
 */
export const LazySpinner = (customProps: LazySpinnerProps) => {
  const { errorJsx, loadingJsx, ...props } = { ...lazyDefaultProps, ...customProps }

  return (
    <ErrorBoundary fallback={errorJsx || <DefaultFallback {...props} />}>
      <Suspense fallback={loadingJsx || <DefaultFallback {...props} />}>
        <Spinner {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
